import { MathUtil } from "@base/math/MathUtil";
import { getKeys, PartialRecord, Prim } from "@base/util/TypeUtil";
import { getArticleName, IAdditionalArticleInfoItem, IArticle } from "@biz/definitions/ArticleDefinitions";
import { EdgeMountItemType, EdgeName, MaterialType, PoleId, PoleType } from "@biz/definitions/SunSailDefinitions";
import { getPointName } from "@biz/definitions/SunSailDefinitions.util";
import { LanguageId } from "@biz/language/Languages";
import { ILocaContext, LocaContext } from "@biz/language/LocaContext";
import { Measure } from "@biz/logic/Measure";
import { IColorItem, SunSailColorProvider } from "@biz/SunSailColors";

export class AdditionalArticleInfo {
  static headerAndText(context: { lc: ILocaContext }, headerId: LanguageId, text: string | string[]): IAdditionalArticleInfoItem {
    return { header: context.lc.t(headerId), text }
  }

  static itemMountType(context: { lc: ILocaContext }, poleType: PoleType, material: MaterialType): IAdditionalArticleInfoItem {
    let text: string = "Undefined";
    if (poleType === PoleType.Wall)
      text = context.lc.t("Material_Wall");
    else if (poleType === PoleType.Pole)
      text = context.lc.t(LocaContext.connect("Material", material))
    return AdditionalArticleInfo.headerAndText(context, "ArticleInfo_ItemMountType", text)
  }

  static material(context: { lc: ILocaContext }, material: MaterialType): IAdditionalArticleInfoItem {
    return AdditionalArticleInfo.headerAndText(context, "ArticleInfo_PoleMaterial", context.lc.t(LocaContext.connect("Material", material)))
  }

  // static colorName(context: { lc: ILocaContext, colorMap: Record<string, IColorItem> }, colorName: string): IAdditionalArticleInfoItem {
  //   return { header: context.lc.t("ArticleInfo_Color"), text: colorName.toString() || "" }
  // }

  static getColorName(colorId: string, lc: ILocaContext) {
    if (!colorId)
      return "";
    const colorItem: IColorItem | undefined = SunSailColorProvider.getColorById(colorId);

    const colorName = colorItem?.displayName || lc.t(LocaContext.connect("ArticleColor", colorId)) || colorId;
    return colorName;
  }

  static colorId(context: { lc: ILocaContext, colorMap: Record<string, IColorItem> }, colorId: string): IAdditionalArticleInfoItem {
    return AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Color", AdditionalArticleInfo.getColorName(colorId, context.lc))
  }

  static rainCut(context: { lc: ILocaContext }, rainCut: boolean): IAdditionalArticleInfoItem {
    return AdditionalArticleInfo.headerAndText(context, "ArticleInfo_RainCut", rainCut ? context.lc.t("Sail_Yes") : context.lc.t("Sail_No"))
  }

  static edgeMountItems(context: { lc: ILocaContext }, edgeMountItems: PartialRecord<EdgeName, EdgeMountItemType>): IAdditionalArticleInfoItem {
    return AdditionalArticleInfo.headerAndText(context, "ArticleInfo_EdgeItems", getKeys(edgeMountItems).map((k) => {
      const edgeMountItemType = edgeMountItems[k];
      return `${k}: ${context.lc.t(LocaContext.connect("SC_EdgeMounItem", edgeMountItemType!))}`
    }))
  }

  static pointNameBeos(context: { lc: ILocaContext }, poleId: PoleId): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_PointName"), text: getPointName(poleId), filter: ["posExtraText"] }
  }

  static lengthToMM(context: { lc: ILocaContext }, lengthInM: number, options?: { headerId?: LanguageId }): IAdditionalArticleInfoItem {
    return { header: context.lc.t(options?.headerId || "ArticleInfo_Length"), text: Measure.toMM(lengthInM) }
  }

  static lengthInM(context: { lc: ILocaContext }, lengthInM: number, options?: { headerId?: LanguageId }): IAdditionalArticleInfoItem {
    return { header: context.lc.t(options?.headerId || "ArticleInfo_Length"), text: (lengthInM.toString() || "") + Measure.m }
  }

  static usage(context: { lc: ILocaContext }, usageId: LanguageId): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_Usage"), text: context.lc.t(usageId) }
  }

  static angleRad(context: { lc: ILocaContext }, angleInRad: number): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_Angle"), text: "" + MathUtil.rad2deg(angleInRad).toFixed(0) + "°" }
  }

  static diameterId(context: { lc: ILocaContext }, val: Prim): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_Diameter"), text: "" + val }
  }

  static diameterMM(context: { lc: ILocaContext }, val: Prim): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_Diameter"), text: "⌀" + (val as number) + Measure.mm }
  }

  static areaM2(context: { lc: ILocaContext }, val: Prim): IAdditionalArticleInfoItem {
    return { header: context.lc.t("ArticleInfo_Area"), text: (val as number).toFixed(1) + Measure.m2 }
  }

  static startTerminal(context: { lc: ILocaContext, articleMap: Record<string, IArticle> }, val: Prim): IAdditionalArticleInfoItem | undefined {
    return val ? { header: context.lc.t("ArticleInfo_StartTerminal"), text: `${getArticleName(context.articleMap[val as string], context.lc.languageType)} (${val as string})` } : undefined
  }

  static endTerminal(context: { lc: ILocaContext, articleMap: Record<string, IArticle> }, val: Prim): IAdditionalArticleInfoItem | undefined {
    return val ? { header: context.lc.t("ArticleInfo_EndTerminal"), text: `${getArticleName(context.articleMap[val as string], context.lc.languageType)} (${val as string})` } : undefined
  }
}

